import React, { useEffect, useMemo, useState } from 'react'
import Breadcrumbs from 'components/Breadcrumbs'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useDispatch, useSelector } from 'react-redux'
import format from 'utils/dateFns/format'
import { parse } from 'date-fns'
import { Button, Col, Grid, Typography } from 'gu-ui-kit'
import styles from 'modules/Recommendations/CampaignsRecommendations/Type/recommendation-type.module.scss'
import clsx from 'clsx'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import {
  CLEAR_FILTERS,
  SET_FILTERS,
} from 'modules/Recommendations/CampaignsRecommendations/Type/types'
import filterConfigs from './filters'
import List from './subcomponents/List'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Helmet } from 'react-helmet'

const TYPES_WITH_FULLTEXT = ['strategy-learning']

const Type = () => {
  const { type } = useParams()
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()
  const { types, filters } = useSelector((state) => ({
    types: state['recommendations/types'].data,
    filters: state['recommendations/type'].filters,
  }))

  const dispatch = useDispatch()
  const typeData = useMemo(
    () => types.find((item) => item.type === type),
    [types, type],
  )

  const filterManager = useFiltersManager({
    filters,
    config: filterConfigs[type],
    setAction: SET_FILTERS,
    localStorageKey: `recommendation-type-${type}`,
  })

  const [openedFulltext, setFulltextOpened] = useState(false)

  useEffect(() => {
    return () => {
      dispatch({
        type: CLEAR_FILTERS,
      })
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>
          {t(`recommendations::types_list.types.${type}.header`)}
          {' — '} {t('recommendations::header')}
        </title>
      </Helmet>
      <Breadcrumbs
        links={[
          {
            title: t('recommendations::breadcrumbs.recommendations_root'),
            to: pathGen(ROUTES.RECOMMENDATIONS),
          },
          {
            title: t('recommendations::breadcrumbs.recommendations'),
            to: pathGen(ROUTES.RECOMMENDATIONS),
          },
          {
            title: t(`recommendations::types_list.types.${type}.header`),
          },
        ]}
      />
      <div className={'margin-bottom-24'}>
        <Typography
          component={'div'}
          variant={'Header-40'}
          className={clsx(
            styles['recommendation-type__header'],
            'margin-bottom-16',
          )}
        >
          {t(`recommendations::types_list.types.${type}.header`)}
        </Typography>
        <Typography variant={'Body-20'} color={'Label-Secondary'}>
          {!!typeData?.date &&
            t('recommendations::type.type_date', {
              date: format(
                parse(typeData.date, 'yyyy-MM-dd', new Date()),
                'dd.MM.yyyy',
              ),
            })}
        </Typography>
      </div>

      <FilterPanel
        filterManager={filterManager}
        className={clsx(
          styles['recommendation-type__filters'],
          'margin-bottom-24',
        )}
        ControlWrapper={({ children }) => (
          <Col s="12" fb={'6'} md={'3'}>
            {children}
          </Col>
        )}
      />

      <Grid
        gap={12}
        direction={'column'}
        className={clsx(styles['recommendation-type__notification'], {
          ['margin-bottom-64']: breakpoints.fb,
          ['margin-bottom-40']: !breakpoints.fb,
        })}
      >
        <Typography
          component={'div'}
          variant={'Body-15'}
          color={'Label-Fifth'}
          className={clsx(styles['recommendation-type__notification-text'])}
          dangerouslySetInnerHTML={{
            __html: t('recommendations::type.notification', {
              context: type,
            }).replace('\\n', '<br />'),
          }}
        />

        {TYPES_WITH_FULLTEXT.includes(type) && (
          <div className={styles['recommendation-type__notification-fulltext']}>
            <Typography
              component={'div'}
              variant={'Body-15'}
              color={'Label-Fifth'}
              className={clsx(
                styles['recommendation-type__notification-text'],
                {
                  [styles['recommendation-type__notification-text_opened']]:
                    openedFulltext,
                },
              )}
              dangerouslySetInnerHTML={{
                __html: t('recommendations::type.notification_fulltext', {
                  context: type,
                }).replace('\\n', '<br />'),
              }}
            />
            <Button
              variant={'link'}
              color={'Label-GU-Primary'}
              size={'s'}
              onClick={() => setFulltextOpened(!openedFulltext)}
            >
              {t('recommendations::type.fulltext', {
                context: openedFulltext ? 'close' : 'open',
              })}
            </Button>
          </div>
        )}
      </Grid>

      <List type={type} filterManager={filterManager} />
    </>
  )
}

export default Type
